import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { allProducts, getProductById } from '../data/products';

const ShopContext = createContext();
const STORAGE_KEY = 'tsu-shop-state-v1';

const loadState = () => {
  if (typeof window === 'undefined') return { cart: [], wishlist: [] };
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : { cart: [], wishlist: [] };
  } catch (err) {
    console.warn('Unable to read saved cart', err);
    return { cart: [], wishlist: [] };
  }
};

export function ShopProvider({ children }) {
  const [cart, setCart] = useState(() => loadState().cart || []);
  const [wishlist, setWishlist] = useState(() => loadState().wishlist || []);

  useEffect(() => {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ cart, wishlist })
    );
  }, [cart, wishlist]);

  const addToCart = (productId, options = {}) => {
    const product = getProductById(productId);
    if (!product) return;

    const size = options.size || product.sizeOptions?.[0] || 'One Size';
    const color = options.color || product.colorOptions?.[0] || 'Default';
    const variantId = `${productId}__${size}__${color}`;

    setCart((current) => {
      const existing = current.find((item) => item.id === variantId);
      if (existing) {
        return current.map((item) =>
          item.id === variantId
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [
        ...current,
        {
          id: variantId,
          productId,
          size,
          color,
          quantity: 1,
        },
      ];
    });
  };

  const updateQuantity = (variantId, quantity) => {
    setCart((current) =>
      quantity <= 0
        ? current.filter((item) => item.id !== variantId)
        : current.map((item) =>
            item.id === variantId ? { ...item, quantity } : item
          )
    );
  };

  const removeFromCart = (variantId) => {
    setCart((current) => current.filter((item) => item.id !== variantId));
  };

  const clearCart = () => setCart([]);

  const toggleWishlist = (productId) => {
    setWishlist((current) =>
      current.includes(productId)
        ? current.filter((id) => id !== productId)
        : [...current, productId]
    );
  };

  const cartItems = useMemo(
    () =>
      cart
        .map((item) => {
          const product = getProductById(item.productId || item.id);
          if (!product) return null;
          return {
            ...product,
            quantity: item.quantity,
            size: item.size,
            color: item.color,
            variantKey: item.id,
          };
        })
        .filter(Boolean),
    [cart]
  );

  const wishlistItems = useMemo(
    () =>
      wishlist
        .map((id) => getProductById(id))
        .filter(Boolean),
    [wishlist]
  );

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const wishlistCount = wishlistItems.length;
  const cartTotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const value = {
    allProducts,
    cartItems,
    wishlistItems,
    cartCount,
    wishlistCount,
    cartTotal,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    toggleWishlist,
    isInWishlist: (productId) => wishlist.includes(productId),
  };

  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
}

export const useShop = () => useContext(ShopContext);
